// ----- config / helpers -----
const BLOCKED = (s="") => s.includes("{H}") || /trending/i.test(s) || /add-?on/i.test(s);

const enableEl = (el) => { el.disabled = false; el.classList.remove("disabled","is-disabled"); el.style.removeProperty("pointer-events"); el.style.removeProperty("opacity"); };
const disableEl = (el) => { el.disabled = true; el.classList.add("disabled","is-disabled"); el.style.pointerEvents = "none"; el.style.opacity = "0.5"; };

const productCache = new Map(); // handle -> Promise(productJSON)

// Try to pull the product handle from the cart item link
function getHandleFromItem(item) {
  const link = item.querySelector('a[href*="/products/"]');
  if (!link) return null;
  try {
    const url = new URL(link.href, location.origin);
    // /products/<handle> or /products/<handle>?variant=...
    const parts = url.pathname.split("/").filter(Boolean);
    const i = parts.indexOf("products");
    return i !== -1 && parts[i+1] ? parts[i+1] : null;
  } catch { return null; }
}

// Optional: try to detect the variant id (nice to have, not required)
function getVariantIdFromItem(item) {
  const byAttr = item.getAttribute("data-variant-id") ||
                 item.querySelector("[data-variant-id]")?.getAttribute("data-variant-id");
  if (byAttr) return Number(byAttr);
  const link = item.querySelector('a[href*="/products/"]');
  if (!link) return null;
  try {
    const url = new URL(link.href, location.origin);
    const v = url.searchParams.get("variant");
    return v ? Number(v) : null;
  } catch { return null; }
}

function fetchProduct(handle) {
  if (!handle) return Promise.resolve(null);
  if (!productCache.has(handle)) {
    productCache.set(handle, fetch(`/products/${handle}.js`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null));
  }
  return productCache.get(handle);
}

// Return true if there exists at least one NON-blocked plan for this product (and variant, if we can check)
function hasAllowedPlans(productJSON, variantId = null) {
  if (!productJSON || !Array.isArray(productJSON.selling_plan_groups)) return false;

  // (If you only want plans from your subscription app, filter groups by app_id here.)
  // const groups = productJSON.selling_plan_groups.filter(g => String(g.app_id) === "4836205");
  const groups = productJSON.selling_plan_groups;

  // Some themes include variant-level eligibility; if present, respect it, else assume plan applies.
  const allowed = [];
  for (const g of groups) {
    if (!Array.isArray(g.selling_plans)) continue;
    for (const p of g.selling_plans) {
      const name = (p.name || "").trim();
      if (!name || BLOCKED(name)) continue;

      // If allocations exist on the plan or group, check variant eligibility (many JSONs don't include it).
      // If not present, we keep the plan.
      if (variantId && Array.isArray(p?.allocation_limit?.eligible_variant_ids)) {
        if (!p.allocation_limit.eligible_variant_ids.includes(variantId)) continue;
      }
      allowed.push(p);
    }
  }
  return allowed.length > 0;
}

// Apply decision to one cart item
async function reconcileItemByProductPlans(item) {
  const sub = item.querySelector(".rebuy-cart__flyout-item-subscription");
  if (!sub) return;

  // Always remove any 5% buttons, per your rule
  sub.querySelectorAll("button.rebuy-button").forEach(btn => {
    if ((btn.textContent || "").includes("5%")) btn.remove();
  });

  const btns = sub.querySelectorAll("button.rebuy-button, button[aria-label*='Subscription']");
  if (!btns.length) return;

  const handle = getHandleFromItem(item);
  const variantId = getVariantIdFromItem(item);

  // pessimistic: don't disable until we *know* there are no allowed plans
  btns.forEach(enableEl);

  const product = await fetchProduct(handle);
  const ok = hasAllowedPlans(product, variantId);

  btns.forEach(btn => ok ? enableEl(btn) : disableEl(btn));
  // If you'd rather hide than disable, use: btn.style.display = ok ? "" : "none";
}

// Backstop: block clicks if only blocked plans exist (prevents passing to checkout)
document.addEventListener("click", async (e) => {
  const btn = e.target.closest(".rebuy-cart__flyout-item-subscription button.rebuy-button, button[aria-label*='Subscription']");
  if (!btn) return;
  const item = btn.closest(".rebuy-cart__flyout-item");
  if (!item) return;

  const handle = getHandleFromItem(item);
  const variantId = getVariantIdFromItem(item);
  const product = await fetchProduct(handle);

  if (!hasAllowedPlans(product, variantId)) {
    e.preventDefault();
    e.stopPropagation();
    // Optional UX: toast/snackbar
    // alert("No valid subscription frequencies are available for this item.");
    return false;
  }
}, { capture: true });

// Run on load and when Rebuy mutates the cart
function runAll() {
  document.querySelectorAll(".rebuy-cart__flyout-item").forEach(reconcileItemByProductPlans);
}
const mo = new MutationObserver(() => runAll());
mo.observe(document.body, { childList: true, subtree: true });
["rebuy:cart.enriched","rebuy:cart.change","rebuy:smartcart.show","rebuy.loaded","onsite-event-publish"]
  .forEach(evt => window.addEventListener(evt, runAll));
document.addEventListener("DOMContentLoaded", runAll);

/*** UI cleanup for plan selector ***/

// Blocked/allowed names (tweak as needed)
const BLOCK_LIST = [
  /\{\s*H\s*\}/i,      // "{H}" or "{{H}}"
  /\{\{H\}\}/i,
  /trending/i,
  /add-?on/i
];

// If you want to *only* show certain plans, add patterns here (leave [] to allow everything not blocked).
// Example to allow months only: const ALLOW_LIST = [/month/i];
const ALLOW_LIST = [];

const isBlockedText = (t = "") => {
  const txt = (t || "").trim();
  if (!txt) return true; // hide empties/placeholders
  // whitelist (optional)
  if (ALLOW_LIST.length && !ALLOW_LIST.some(re => re.test(txt))) return true;
  // blacklist
  return BLOCK_LIST.some(re => re.test(txt));
};

function cleanSelectorsInItem(item) {
  const sub = item.querySelector(".rebuy-cart__flyout-item-subscription");
  if (!sub) return;

  sub.querySelectorAll("select.rebuy-select").forEach((select) => {
    // Remove blocked options (walk backwards)
    for (let i = select.options.length - 1; i >= 0; i--) {
      const opt = select.options[i];
      if (isBlockedText(opt.textContent)) opt.remove();
    }

    // Remove empty optgroups (if Rebuy uses them)
    select.querySelectorAll("optgroup").forEach((og) => {
      if (!og.querySelector("option")) og.remove();
    });

    // If nothing left, disable the control
    const validOpts = Array.from(select.options).filter(
      (o) => !o.disabled && (o.textContent || "").trim()
    );
    if (validOpts.length === 0) {
      select.disabled = true;
      return;
    }

    // Ensure a valid selection and notify Rebuy
    const cur = select.options[select.selectedIndex];
    const curBad = !cur || cur.disabled || isBlockedText(cur.textContent);
    if (curBad) {
      const idx = Array.from(select.options).findIndex(
        (o) => !o.disabled && !isBlockedText(o.textContent)
      );
      if (idx !== -1) {
        select.selectedIndex = idx;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  });
}

// Run after Rebuy writes/updates the cart
function cleanAllSelectors() {
  document.querySelectorAll(".rebuy-cart__flyout-item").forEach(cleanSelectorsInItem);
}

// Observe cart mutations (Rebuy hydrates in waves)
const cleanupObserver = new MutationObserver(() => {
  clearTimeout(cleanupObserver._t);
  cleanupObserver._t = setTimeout(cleanAllSelectors, 120);
});
cleanupObserver.observe(document.body, { childList: true, subtree: true });

// Event hooks you’re already using
[
  "rebuy:cart.enriched",
  "rebuy:cart.change",
  "rebuy:smartcart.show",
  "rebuy.loaded",
  "onsite-event-publish",
].forEach((evt) => window.addEventListener(evt, cleanAllSelectors));

document.addEventListener("DOMContentLoaded", cleanAllSelectors);